/**
 * The COA department rows (USALI section, statement caption, drag order) and
 * the % Rev / GOP pairing links for an org — the two org-scoped inputs the
 * Financial report's classifier reads. Fetched once per org/location and
 * shared by every forecast page that deals departments into statement bands.
 */

import { useEffect, useState } from 'react';
// @ts-ignore - JavaScript module without types
import { getChartOfAccountDepartments, getCoaRevenueBasisMappings } from '../../../utils/api';
import type { DepartmentMetaRow, RevenueBasisLinkRow } from '../utils/usaliLayout';
import { normalizeDeptName } from '../utils/usaliLayout';

export interface UsaliMetadata {
  departments: DepartmentMetaRow[];
  revenueBasisLinks: RevenueBasisLinkRow[];
  /** Normalised department name → presentation order (location override wins). */
  deptOrder: Map<string, number>;
}

export function useUsaliMetadata(orgId: number | null | undefined, locationId: number | null | undefined): UsaliMetadata {
  const [departments, setDepartments] = useState<DepartmentMetaRow[]>([]);
  const [revenueBasisLinks, setRevenueBasisLinks] = useState<RevenueBasisLinkRow[]>([]);
  const [deptOrder, setDeptOrder] = useState<Map<string, number>>(new Map());

  useEffect(() => {
    let cancelled = false;
    if (!orgId) {
      setDepartments([]);
      setRevenueBasisLinks([]);
      setDeptOrder(new Map());
      return undefined;
    }
    Promise.all([
      getChartOfAccountDepartments(orgId, locationId || undefined),
      getCoaRevenueBasisMappings(orgId),
    ])
      .then(([rows, links]: [DepartmentMetaRow[], RevenueBasisLinkRow[]]) => {
        if (cancelled) return;
        const depts = rows || [];
        setDepartments(depts);
        setRevenueBasisLinks(links || []);
        // A location with ANY override rows uses its own order exclusively.
        const hasLocationOrder = depts.some((d) => d.locationDisplayOrder != null);
        const order = new Map<string, number>();
        depts.forEach((d) => {
          const raw = hasLocationOrder ? d.locationDisplayOrder : d.displayOrder;
          if (raw == null || !Number.isFinite(Number(raw))) return;
          const key = normalizeDeptName(String(d.name || ''));
          if (key && !order.has(key)) order.set(key, Number(raw));
        });
        setDeptOrder(order);
      })
      .catch(() => {
        if (cancelled) return;
        setDepartments([]);
        setRevenueBasisLinks([]);
        setDeptOrder(new Map());
      });
    return () => { cancelled = true; };
  }, [orgId, locationId]);

  return { departments, revenueBasisLinks, deptOrder };
}
